import { FC, useState, useEffect } from 'react';

import { styles } from './styles';
import { QAItem } from 'types/api';
import { DocumentCategory } from 'types/api';

import { Box } from 'ui/Box';
import { Typography } from 'ui/Typography';

import { Button } from 'components/Button';
import { Select } from 'components/Select';
import { Input } from 'components/Input';
import { TextArea } from 'components/TextArea';

import { QAButtons } from '../QAButtons';
import clsx from 'clsx';

export interface AddQAFormProps {
  editItem?: QAItem;
  disabled?: boolean;
  onAddQAOpen: () => void;
  onAddQAClose: () => void;
  onUploadDocument: () => void;
  categories: DocumentCategory[];
  departments: Omit<DocumentCategory, 'created_at'>[];
  url?: string;
  onSubmit: ({
    question,
    answer,
    category,
    department,
  }: {
    question: string;
    answer: string;
    category: number;
    department: number;
  }) => void;
}
export const AddQAForm: FC<AddQAFormProps> = ({
  url,
  editItem,
  onSubmit,
  categories,
  departments,
  onAddQAOpen,
  onAddQAClose,
  onUploadDocument,
  disabled = false,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(
    editItem?.category?.id.toString() || ''
  );
  const [selectedDepartment, setSelectedDepartment] = useState(
    editItem?.department?.id.toString() || ''
  );

  const [question, setQuestion] = useState(editItem?.question || '');
  const [answer, setAnswer] = useState(editItem?.answer || '');
  const [errors, setErrors] = useState({
    question: '',
    answer: '',
    category: '',
    department: '',
  });

  const mappedCategories = categories.map(({ id, name }) => ({
    label: name,
    value: id.toString(),
  }));
  const mappedDepartments = departments.map(({ id, name }) => ({
    label: name,
    value: id.toString(),
  }));
  const onDepartmentChange = (value: string | string[]) => {
    setSelectedDepartment(value as string);
  };

  const onCategoryChange = (value: string | string[]) => {
    setSelectedCategory(value as string);
  };
  const onFormSubmit = () => {
    setErrors({
      question: '',
      answer: '',
      category: '',
      department: '',
    });
    if (!question && !url) {
      setErrors((prev) => ({ ...prev, question: 'Question is required' }));
    }
    if (!answer && !url) {
      setErrors((prev) => ({ ...prev, answer: 'Answer is required' }));
    }
    if (!selectedCategory && !url) {
      setErrors((prev) => ({ ...prev, category: 'Category is required' }));
      return;
    }
    if (!selectedDepartment && !url) {
      setErrors((prev) => ({ ...prev, department: 'Department is required' }));
      return;
    }

    if (question.length > 5000) {
      setErrors((prev) => ({
        ...prev,
        question: 'Question should be less than 5000 characters',
      }));
      return;
    }
    if (answer.length > 5000) {
      setErrors((prev) => ({
        ...prev,
        answer: 'Answer should be less than 5000 characters',
      }));
      return;
    }

    onSubmit({
      question,
      answer,
      category: Number(selectedCategory),
      department: Number(selectedDepartment),
    });
  };

  useEffect(() => {
    const isMac = /Mac/.test(navigator.platform);
    if (!isMac) return;

    const onPopState = () => {
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const isSaveDisabled = url
    ? false
    : !question || !answer || !selectedCategory || !selectedDepartment;

  return (
    <Box className="flex flex-1 flex-col border-mult md:border md:p-8">
      <QAButtons
        onAddQAOpen={onAddQAOpen}
        onUploadDocument={onUploadDocument}
      />
      <Box className="mt-8 flex h-full flex-1 flex-col gap-[10px]">
        {url ? (
          <Box
            component={'iframe'}
            src={url}
            title="file"
            style={{
              width: '100%',
              overflow: 'auto',
              border: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
            allowFullScreen
            className="min-h-[400px] border border-mult"
          />
        ) : (
          <>
            <Input
              value={question}
              className="w-full"
              error={!!errors.question}
              helperText={errors.question}
              placeholder="Write a Question"
              onChange={(event) => setQuestion(event.target.value)}
            />

            <TextArea
              value={answer}
              sx={styles.textArea}
              error={!!errors.answer}
              helperText={errors.answer}
              placeholder="Write an Answer"
              onChange={(event) => setAnswer(event.target.value)}
            />
          </>
        )}
        {!url && (
          <Box className="my-6 flex flex-col gap-4 md:max-w-[450px] md:gap-2">
            <Box className="flex flex-wrap items-center gap-x-6 gap-y-2 md:flex-nowrap">
              <Typography
                variant="bodyText"
                className="min-w-20 font-semibold text-black"
              >
                Category
              </Typography>
              <Select
                disabled={!!url}
                error={errors.category}
                value={selectedCategory}
                options={mappedCategories}
                onChange={onCategoryChange}
              />
            </Box>
            <Box className="flex flex-wrap items-center gap-x-6 gap-y-2 md:flex-nowrap">
              <Typography
                variant="bodyText"
                className="min-w-20 font-semibold text-black"
              >
                Department
              </Typography>
              <Select
                disabled={!!url}
                error={errors.department}
                value={selectedDepartment}
                options={mappedDepartments}
                onChange={onDepartmentChange}
              />
            </Box>
          </Box>
        )}
        <Box
          className={clsx(
            'flex flex-wrap items-center gap-[10px] md:mx-auto md:flex-nowrap',
            url && 'mt-auto'
          )}
        >
          <Button
            disabled={disabled}
            variantType="outlined"
            onClick={onAddQAClose}
            className="w-full md:max-w-[120px]"
          >
            <Typography>Cancel</Typography>
          </Button>
          <Button
            onClick={onFormSubmit}
            className="w-full md:max-w-[120px]"
            disabled={isSaveDisabled || disabled}
          >
            <Typography>Save</Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
